import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import classNames from 'classnames';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import MaxWidth from '@/components/common/layout/MaxWidth';
import { getMediaUrlForActiveConnection } from '@/components/templates/seasonal-guides/utils';
import { useBreakpoint } from '@/hooks/useBreakpoint';

import css from './outdoorsyGroupStyles.module.css';

const heroMediaFallbackUrl = '/assets/seasonal-guides/summer/hero-media-fallback.jpg';
const heroMediaHighBitrateUrl = '/assets/seasonal-guides/summer/hero-media-high-bitrate.mp4';
const heroMediaMedBitrateUrl = '/assets/seasonal-guides/summer/hero-media-med-bitrate.mp4';

const Hero = () => {
  const intl = useIntl();
  const [heroMediaUrl, setHeroMediaUrl] = useState<string>();
  useEffect(
    () =>
      setHeroMediaUrl(
        getMediaUrlForActiveConnection(heroMediaMedBitrateUrl, heroMediaHighBitrateUrl),
      ),
    [],
  );
  const { isAboveTablet, isAboveDesktop } = useBreakpoint();

  const fallbackMedia = (
    <Image
      src={heroMediaFallbackUrl}
      alt="The Outdoorsy Group"
      role="presentation"
      layout="fill"
      objectFit="cover"
      objectPosition="center"
      placeholder="blur"
      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEASABIAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAMCAgMCAgMDAwMEAwMEBQgFBQQEBQoHBwYIDAoMDAsKCwsNDhIQDQ4RDgsLEBYQERMUFRUVDA8XGBYUGBIUFRT/2wBDAQMEBAUEBQkFBQkUDQsNFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBT/wAARCAAKABIDASIAAhEBAxEB/8QAFwAAAwEAAAAAAAAAAAAAAAAAAAYIBP/EACgQAAEDAwEHBQEAAAAAAAAAAAECAwQABRESBgchJDFBURQVIoGS4f/EABYBAQEBAAAAAAAAAAAAAAAAAAQAAv/EABkRAAIDAQAAAAAAAAAAAAAAAAABAhESMf/aAAwDAQACEQMRAD8Ay3qDHi7t9pliC3HlR2pKWAllIUlKc6NJ8EAH7qELpfrpcrHZZN1jKQ0445m4FhLK1qGdKcgJBHXPUjhx7Gwd8c6TDiQhHkOsBxSwsNrKdQ8HHWpo2lPuFyUuVzKwgpCnvmQPGTTE7gmD5NoQFXBBUT6wjPbV/aKZTAjZPLtfgUVaNUf/2Q=="
      priority={true}
      quality={75}
    />
  );

  const headingVariant = isAboveDesktop ? EHeadingStyleVariant.H2Alt : EHeadingStyleVariant.H3Alt;

  return (
    <section>
      <div className="py-20 text-center lg:py-24 xxxl:py-[172px]">
        <Text className="pb-2 md:pb-4" variant={ETextStyleVariant.LargeRegular}>
          The
        </Text>
        <div className="font-medium text-center text-gray-800 text-[48px] md:text-[80px]">
          Outdoorsy
          <span className="block md:inline"> </span>
          Group
        </div>
      </div>

      <div className={classNames('relative text-white h-[844px] lg:h-[575px]: xxxl:h-[809px]')}>
        <div className="absolute inset-0">{fallbackMedia}</div>

        {heroMediaUrl && (
          <div className="absolute inset-0">
            <video
              autoPlay
              playsInline
              loop
              muted
              className="object-cover w-full h-[844px] lg:h-[575px]: xxxl:h-[809px]">
              <source src={heroMediaUrl} type="video/mp4" />
              {fallbackMedia}
            </video>
          </div>
        )}

        <div className={`absolute inset-0 ${css.heroBackgorund}`}>
          <MaxWidth className="justify-end h-full pb-20 mx-auto md:pb-0 md:justify-center gap-y-6 md:items-center md:text-center">
            <Heading variant={isAboveTablet ? EHeadingStyleVariant.H5 : EHeadingStyleVariant.H6}>
              <FormattedMessage defaultMessage="Our mission" id="QB3gU8" />
            </Heading>
            <Heading className="py-6 lg:py-10" variant={headingVariant}>
              <FormattedMessage
                defaultMessage="Restore people's relationship with the outdoors and each other by solving foundational human problems and creating lasting memories through exceptional outdoor experiences."
                id="2hx8Ux"
              />
            </Heading>
            <Button
              className="w-fit"
              href="https://www.linkedin.com/pulse/milestone-our-journey-3-billion-salesthank-you-your-support-luttc/?trackingId=mFP4w49s0taFdXxBqLzaVg%3D%3D"
              target="blank"
              variant={EButtonColorVariant.Secondary}
              label={intl.formatMessage({ defaultMessage: 'Letter from our CEO', id: '3veTM+' })}
              onDark
            />
          </MaxWidth>
        </div>
      </div>
    </section>
  );
};

export default Hero;
